/**
 * Nav icon names → Material icons.
 *
 * `NavItem` carries a name rather than a component so the navigation model stays plain data (it
 * is read by the route metadata too, which has no business importing icons). One map, one icon
 * set — outlined throughout, because a rail of mixed filled and outlined icons reads as an
 * accident.
 */

import type { ReactNode } from 'react';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';

const SIZE = { fontSize: 20 };

const ICONS: Record<string, ReactNode> = {
  home: <HomeOutlinedIcon sx={SIZE} />,
  receipt: <ReceiptLongOutlinedIcon sx={SIZE} />,
  'person-receipt': <ReceiptOutlinedIcon sx={SIZE} />,
  person: <PersonOutlineOutlinedIcon sx={SIZE} />,
  help: <HelpOutlineOutlinedIcon sx={SIZE} />,
  card: <CreditCardOutlinedIcon sx={SIZE} />,
  unmatched: <LinkOffOutlinedIcon sx={SIZE} />,
  scales: <BalanceOutlinedIcon sx={SIZE} />,
  cash: <PaymentsOutlinedIcon sx={SIZE} />,
  stock: <WarehouseOutlinedIcon sx={SIZE} />,
  categories: <AccountTreeOutlinedIcon sx={SIZE} />,
  products: <Inventory2OutlinedIcon sx={SIZE} />,
  variants: <StyleOutlinedIcon sx={SIZE} />,
  'find-unit': <SearchOutlinedIcon sx={SIZE} />,
  suppliers: <StorefrontOutlinedIcon sx={SIZE} />,
  purchases: <LocalShippingOutlinedIcon sx={SIZE} />,
  upload: <UploadFileOutlinedIcon sx={SIZE} />,
  people: <GroupOutlinedIcon sx={SIZE} />,
  shield: <AdminPanelSettingsOutlinedIcon sx={SIZE} />,
  storefront: <StorefrontOutlinedIcon sx={SIZE} />,
  history: <HistoryOutlinedIcon sx={SIZE} />,

  // Phase 4. `transfers` gets the two-way arrow rather than another lorry: a transfer is between
  // our own shops, and reusing the Purchases lorry would make the two read as the same thing.
  transfers: <CompareArrowsOutlinedIcon sx={SIZE} />,
  counts: <AssignmentTurnedInOutlinedIcon sx={SIZE} />,
  'write-offs': <TrendingDownOutlinedIcon sx={SIZE} />,
  faulty: <BuildOutlinedIcon sx={SIZE} />,
  claims: <GavelOutlinedIcon sx={SIZE} />,
  'online-orders': <ShoppingBagOutlinedIcon sx={SIZE} />,
  'online-listings': <PhotoLibraryOutlinedIcon sx={SIZE} />,
  'online-settings': <LanguageOutlinedIcon sx={SIZE} />,
  settlements: <HandshakeOutlinedIcon sx={SIZE} />,
  'price-bulk': <PriceChangeOutlinedIcon sx={SIZE} />,

  // Phase 5.
  review: <FactCheckOutlinedIcon sx={SIZE} />,
  disputes: <GavelOutlinedIcon sx={SIZE} />,
  approvals: <HowToRegOutlinedIcon sx={SIZE} />,
  brokers: <HandshakeOutlinedIcon sx={SIZE} />,
  customers: <PersonOutlineOutlinedIcon sx={SIZE} />,
  reports: <BarChartOutlinedIcon sx={SIZE} />,

  // Phase 6.
  'print-agent': <PrintOutlinedIcon sx={SIZE} />,
  'print-jobs': <ListAltOutlinedIcon sx={SIZE} />,
  notifications: <NotificationsOutlinedIcon sx={SIZE} />,
};

/** A missing name renders nothing rather than a placeholder — a wrong icon is worse than none. */
export function navIcon(name: string): ReactNode {
  return ICONS[name] ?? null;
}
